// src/components/public/branding/ThemeProvider.tsx
'use client'
import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react'
import { setTrackingCurrency } from '@/lib/timed-ui/analytics'

interface BrandingData {
  colors: {
    primary: string
    secondary: string
    accent: string
    background: string
    foreground: string
    muted: string
    success: string
    warning: string
    error: string
    primaryHover: string
    primaryLight: string
    primaryDark: string
    cardBackground: string
    borderColor: string
  }
  typography: {
    headingFont: string
    bodyFont: string
    headingWeight: number
    bodyWeight: number
    headingSize: string
    bodySize: string
    letterSpacing: string
    lineHeight: string
    fontDisplay: string
    googleFonts: string[]
    fontFallbacks: {
      heading: string
      body: string
    }
  }
  layout: {
    template: string
    headerStyle: string
    buttonStyle: string
    cardStyle: string
    spacing: string
    containerWidth: string
    borderRadius: string
    shadowStyle: string
    navigationStyle: string
  }
  assets: {
    logoUrl?: string | null
    logoUrlLight?: string | null
    secondaryLogoUrl?: string | null
    iconUrl?: string | null
    faviconUrl?: string | null
    socialImageUrl?: string | null
    backgroundImageUrl?: string | null
    logoWidth?: number | null
    logoHeight?: number | null
    heroImageUrl?: string | null
    footerLogoUrl?: string | null
    loadingIconUrl?: string | null
  }
  customizations: {
    customCSS?: string | null
    customHTML?: string | null
    googleFonts?: string[]
    enableAnimations?: boolean
    enableDarkMode?: boolean
    enableRTL?: boolean
    headerHTML?: string | null
    footerHTML?: string | null
    trackingCodes?: {
      googleAnalytics?: string
      googleTagManager?: string
      facebookPixel?: string
      tikTokPixel?: string
      customScripts?: string[]
    }
    seoSettings?: {
      defaultTitle?: string
      titleTemplate?: string
      defaultDescription?: string
      keywords?: string[]
      twitterHandle?: string
      facebookAppId?: string
    }
  }
  publicSite: {
    siteName: string
    siteDescription: string
    heroSubtitle?: string
    contactEmail: string
    socialLinks: {
      facebook?: string
      twitter?: string
      instagram?: string
      linkedin?: string
      youtube?: string
      tiktok?: string
    }
    features: {
      showVenuePages: boolean
      showEventSearch: boolean
      showUpcomingEvents: boolean
      enableWaitlist: boolean
      enableSocialSharing: boolean
    }
    navigation: {
      showAbout: boolean
      showContact: boolean
      showHelp: boolean
      customLinks: Array<{
        label: string
        url: string
        openInNewTab: boolean
      }>
    }
    footer?: {
      columns: Array<{
        heading: string
        links: Array<{ label: string; url: string; openInNewTab: boolean }>
      }>
      showSocialLinks: boolean
      copyright: string
    }
  }
}

interface ClientInfo {
  id: string
  name: string
  /** Tenant's ISO 4217 currency code (USD, EUR, GBP, AUD, CAD, JPY, MXN). */
  currency?: string
}

interface ThemeContextType {
  client: ClientInfo | null
  branding: BrandingData | null
  hasCustomBranding: boolean
  isLoading: boolean
  /** Tenant currency, with USD fallback. Components can pass this to formatPrice. */
  currency: string
  updateBranding: (newBranding: BrandingData) => void
}

const ThemeContext = createContext<ThemeContextType>({
  client: null,
  branding: null,
  hasCustomBranding: false,
  isLoading: true,
  currency: 'USD',
  updateBranding: () => {}
})

interface ThemeProviderProps {
  children: ReactNode
}

// Read cached branding from localStorage (synchronous — prevents color flash)
function getCachedBranding(): { branding: BrandingData; hasCustom: boolean } | null {
  try {
    const raw = localStorage.getItem('__branding_cache')
    if (raw) {
      const parsed = JSON.parse(raw)
      // Support both old format (BrandingData directly) and new format ({ branding, hasCustom })
      if (parsed && parsed.branding) {
        return parsed as { branding: BrandingData; hasCustom: boolean }
      }
      // Old format: just branding data
      return { branding: parsed as BrandingData, hasCustom: true }
    }
  } catch { /* ignore */ }
  return null
}

function setCachedBranding(data: BrandingData, hasCustom: boolean) {
  try {
    localStorage.setItem('__branding_cache', JSON.stringify({ branding: data, hasCustom }))
  } catch { /* ignore */ }
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [client, setClient] = useState<ClientInfo | null>(null)
  // IMPORTANT: do not read localStorage in the initial useState callback.
  // Server renders without localStorage (= null) and client first render
  // would have the cached value, causing a hydration mismatch (React #418
  // and #423). Always start with null on both sides; load cached branding
  // in the useEffect below post-mount.
  const [branding, setBranding] = useState<BrandingData | null>(null)
  const [hasCustomBranding, setHasCustomBranding] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  // Hydrate from cache after mount to avoid hydration mismatch
  useEffect(() => {
    const cached = getCachedBranding()
    if (cached) {
      setBranding(cached.branding)
      setHasCustomBranding(cached.hasCustom)
    }
  }, [])

  // Brand Studio live preview: when the storefront is loaded inside the admin
  // preview iframe (?brandPreview=1), do NOT fetch/cache branding. Instead apply
  // the draft branding pushed from the admin via postMessage on every edit, so
  // the iframe is a faithful, naturally-scoped preview of unsaved changes.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const isPreview = new URLSearchParams(window.location.search).get('brandPreview') === '1'
    if (!isPreview) return

    setIsLoading(false)
    const onMessage = (event: MessageEvent) => {
      const data = event.data
      if (!data || data.type !== 'brand-preview' || !data.branding) return
      setBranding(data.branding as BrandingData)
      setHasCustomBranding(true)
    }
    window.addEventListener('message', onMessage)
    // Tell the parent we're ready so it sends the initial draft.
    try { window.parent?.postMessage({ type: 'brand-preview-ready' }, '*') } catch { /* ignore */ }
    return () => window.removeEventListener('message', onMessage)
  }, [])

  // Load client info and branding data from API (skipped in preview mode)
  useEffect(() => {
    if (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('brandPreview') === '1') {
      return
    }
    const loadBranding = async () => {
      try {
        // Fetch client info
        const clientRes = await fetch('/api/client/current')
        if (clientRes.ok) {
          const clientData = await clientRes.json()
          if (clientData.success && clientData.client) {
            setClient({
              id: clientData.client.id,
              name: clientData.client.name,
              currency: clientData.client.currency || 'USD',
            })
          }
        }

        // Fetch branding
        const response = await fetch('/api/branding?t=' + Date.now())
        if (response.ok) {
          const result = await response.json()
          if (result.success) {
            const hasCustom = result.hasCustomBranding ?? false
            // Ensure template defaults to 'modern' for backward compatibility
            const normalizedBranding = {
              ...result.data,
              layout: {
                ...result.data.layout,
                template: result.data.layout?.template || 'modern'
              }
            }
            setBranding(normalizedBranding)
            setHasCustomBranding(hasCustom)
            setCachedBranding(normalizedBranding, hasCustom)
          }
        }
      } catch (error) {
        console.error('Failed to load branding:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadBranding()

    // Listen for branding updates from admin
    const handleBrandingUpdate = (event: CustomEvent) => {
      setBranding(event.detail)
      setHasCustomBranding(true)
    }

    window.addEventListener('brandingUpdated', handleBrandingUpdate as EventListener)
    
    return () => {
      window.removeEventListener('brandingUpdated', handleBrandingUpdate as EventListener)
    }
  }, [])

  const updateBranding = (newBranding: BrandingData) => {
    setBranding(newBranding)
  }

  // Hand the tenant's currency to the analytics module so pixel and GA4 events
  // report the real currency instead of a hardcoded USD.
  useEffect(() => {
    setTrackingCurrency(client?.currency)
  }, [client?.currency])

  return (
    <ThemeContext.Provider value={{ client, branding, hasCustomBranding, isLoading, currency: client?.currency || 'USD', updateBranding }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error(
      'useTheme must be used within a ThemeProvider. ' +
      'Make sure CartProviders wraps your layout component. ' +
      'Check: src/app/admin/layout.tsx, src/app/boxoffice/layout.tsx, src/app/layout.tsx'
    )
  }
  return context
}

// Default export for compatibility
export default ThemeProvider